import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './style.css';
import { LoadListDefault, useGet } from '@/api/hooks';
import { CreatePostService, GetMedicalRequestDetails } from '@/api/endpoints/Requests';
import { errorToast, successToast } from '@/utils/toasters';
import { Icon } from '@iconify/react/dist/iconify.js';

const PostServiceEntry = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useTranslation();

    const { result: requestDetails, isPending } = useGet({
        ...LoadListDefault,
        endPoint: GetMedicalRequestDetails,
        depArr: [],
        allowFetch: !!state?.requestId,
        params: [state?.requestId]
    });

    const [loading, setLoading] = useState(false);

    const [serviceRecords, setServiceRecords] = useState([
        {
            id: 1,
            name: '',
            price: '',
            reason: ''
        }
    ]);

    const handleAddService = () => {
        setServiceRecords([
            ...serviceRecords,
            {
                id: serviceRecords.length + 1,
                name: '',
                price: '',
                reason: ''
            }
        ]);
    };

    const handleRemoveService = (id) => {
        if (serviceRecords.length > 1) {
            setServiceRecords(serviceRecords.filter(record => record.id !== id));
        }
    };

    const handleServiceChange = (id, field, value) => {
        setServiceRecords(serviceRecords.map(record =>
            record.id === id ? { ...record, [field]: value } : record
        ));
    };

    const calculateTotal = () => {
        return serviceRecords.reduce((sum, record) => {
            return sum + (parseFloat(record.price) || 0);
        }, 0).toFixed(2);
    };

    const handleCancel = () => {
        navigate("/panel/provider/requests/medical")
    };


    const handleSubmit = () => {
        if (serviceRecords.length === 0 || serviceRecords.some(record => record.name === '' || record.price === '' || record.reason === '')) {
            errorToast("error-validation");
            return;
        }
        const data = {
            hospitalOrderId: state?.requestId,
            services: serviceRecords
        }
        setLoading(true);
        CreatePostService(data).
            then(response => {
                successToast("Operation Done Successfully");
                setLoading(false);
                navigate("/panel/provider/requests/medical")
            }).catch(error => {
                setLoading(false);
                console.log('Error', error);
            });
        // Add submission logic
    };

    return (
        <div className="post-service-entry-container">
            {/* Header */}
            <div className="post-service-entry-header">
                <h1 className="post-service-entry-title">{t('post-service-entry')}</h1>
                <p className="post-service-entry-subtitle">
                    {t('record-finalize-clinical-details-one-more-procedures')}
                </p>
            </div>
            <div className="post-service-entry-content">

                {/* Patient Identity */}
                <div className="post-service-patient-identity">
                    <div className="post-service-patient-badge">{t('patient-identity')}</div>
                    <h2 className="post-service-patient-name">{requestDetails?.patientData?.name}</h2>
                    <div className="post-service-patient-meta">
                        <span className="post-service-patient-meta-item">
                            <Icon icon="stash:data-date" width="14" height="14" />
                            {t('age')}: {requestDetails?.patientData?.age}
                        </span>
                        <span className="post-service-patient-meta-item">
                            <Icon icon="mdi:card-account-details" width="14" height="14" />
                            {t('patient-id')}: {requestDetails?.patientData?.id}
                        </span>
                    </div>
                </div>

                {/* Service Categories */}
                <div className="post-service-categories">
                    <div className="post-service-category-card">
                        <div className="post-service-category-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="post-service-category-label">{t('category')}</div>
                        <div className="post-service-category-value">{requestDetails?.requestData?.category}</div>
                    </div>
                    <div className="post-service-category-card">
                        <div className="post-service-category-icon">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                                <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <div className="post-service-category-label">{t('sub-category')}</div>
                        <div className="post-service-category-value">{requestDetails?.requestData?.subCategory}</div>
                    </div>
                </div>
            </div>
            <div className="row">
                {/* Service Records */}
                {serviceRecords.map((record, index) => (
                    <div key={record.id} className={`col-12 ${(index === serviceRecords.length - 1 && index % 2 === 0) ? 'col-12' : 'col-md-6'}`}>
                        <div className={`post-service-record`}>
                            <div className="post-service-record-header">
                                <h3 className="post-service-record-title">
                                    {t('service-record')} #{record.id}
                                </h3>
                                {serviceRecords.length > 1 && (
                                    <button
                                        className="post-service-record-delete"
                                        onClick={() => handleRemoveService(record.id)}
                                    >
                                        <Icon icon="carbon:close-filled" width="24" height="24" />
                                    </button>
                                )}
                            </div>
                            <div className="post-service-record-subtitle">{t('enter-precise-details')}</div>

                            <div className="post-service-record-row">
                                <div className="post-service-record-field">
                                    <label className="post-service-record-label">
                                        {t('specific-service-performed')}
                                    </label>
                                    <input
                                        type="text"
                                        className="post-service-record-input"
                                        placeholder={t('eg-transthoracic-echocardiogram-tte')}
                                        value={record.name}
                                        onChange={(e) => handleServiceChange(record.id, 'name', e.target.value)}
                                    />
                                </div>
                                <div className="post-service-record-field post-service-record-field-cost">
                                    <label className="post-service-record-label">
                                        {t('service-cost')} ($)
                                    </label>
                                    <div className="post-service-record-input-group">
                                        <span className="post-service-record-currency">$</span>
                                        <input
                                            type="number"
                                            className="post-service-record-input post-service-record-input-cost"
                                            placeholder="0.00"
                                            value={record.price}
                                            onChange={(e) => handleServiceChange(record.id, 'price', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="post-service-record-field">
                                <label className="post-service-record-label">
                                    {t('reason-service-clinical-justification')}
                                </label>
                                <textarea
                                    className="post-service-record-textarea"
                                    rows="4"
                                    placeholder={t('enter-clinical-notes-patient-symptoms-justification')}
                                    value={record.reason}
                                    onChange={(e) => handleServiceChange(record.id, 'reason', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="post-service-entry-footer">

                {/* Add Another Service Button */}
                <button className="post-service-add-btn" onClick={handleAddService}>
                    <Icon icon="mdi:plus" width="16" height="16" />
                    {t('add-another-service')}
                </button>

                {/* Footer */}
                <div className="post-service-footer">
                    <div className="post-service-total">
                        <div className="post-service-total-label">{t('estimated-total')}</div>
                        <div className="post-service-total-value">${calculateTotal()}</div>
                    </div>
                    <div className="post-service-actions">
                        <button disabled={loading} className="post-service-cancel-btn" onClick={handleCancel}>
                            {t('cancel-entry')}
                        </button>
                        <button disabled={loading} className="post-service-submit-btn" onClick={handleSubmit}>
                            <Icon icon="mdi:check" width="16" height="16" />
                            {loading ? t('loading') : t('submit-post-service')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer Note */}
            <div className="post-service-footer-note">
                <Icon icon="mdi:plus" width="16" height="16" />
                {t('secure-health-information-system-hipaa-compliant-session')}
            </div>
        </div>
    );
};

export default PostServiceEntry;

